import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getStats } from '../api/client';
import type { StatsResponse } from '../api/types';

export default function Dashboard() {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getStats()
      .then(setStats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* V2 counters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="text-sm text-gray-500 uppercase tracking-wide">
            Pending embed retries
          </div>
          <div
            className={`text-3xl font-bold ${
              stats.pendingEmbedRetries > 0 ? 'text-amber-600' : 'text-green-600'
            }`}
          >
            {stats.pendingEmbedRetries}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            stories with embeddings_ready=0
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-500 uppercase tracking-wide">
            Stuck embeds
          </div>
          <div
            className={`text-3xl font-bold ${
              stats.stuckEmbeds > 0 ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {stats.stuckEmbeds}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            embed_failure_reason set (retries exhausted)
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-500 uppercase tracking-wide">
            Active refinement sessions
          </div>
          <div className="text-3xl font-bold text-pebbl-600">
            {stats.activeRefinementSessions}
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-500 uppercase tracking-wide">
            Doorbell failures (24h)
          </div>
          <div
            className={`text-3xl font-bold ${
              stats.doorbellFailures24h > 0 ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {stats.doorbellFailures24h}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="text-sm text-gray-500 uppercase tracking-wide">Total Stories</div>
          <div className="text-3xl font-bold text-pebbl-600">{stats.totalStories}</div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-500 uppercase tracking-wide">Cities</div>
          <div className="text-3xl font-bold text-pebbl-600">
            {Object.keys(stats.byCity).length}
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-500 uppercase tracking-wide">Story Types</div>
          <div className="text-3xl font-bold text-pebbl-600">
            {Object.keys(stats.byType).length}
          </div>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Stories by City</h3>
          <div className="space-y-2">
            {Object.entries(stats.byCity).map(([city, count]) => (
              <div key={city} className="flex items-center justify-between">
                <span className="text-gray-600">{city}</span>
                <div className="flex items-center">
                  <div
                    className="h-4 bg-pebbl-500 rounded"
                    style={{ width: `${(count / stats.totalStories) * 200}px` }}
                  />
                  <span className="ml-2 text-sm text-gray-500">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Stories by Type</h3>
          <div className="space-y-2">
            {Object.entries(stats.byType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{type.replace('_', ' ')}</span>
                <div className="flex items-center">
                  <div
                    className="h-4 bg-pebbl-400 rounded"
                    style={{ width: `${(count / stats.totalStories) * 200}px` }}
                  />
                  <span className="ml-2 text-sm text-gray-500">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Current Config */}
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-4">Current Configuration</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Min Score</div>
            <div className="font-mono">{stats.config.minScore}</div>
          </div>
          <div>
            <div className="text-gray-500">Min Event</div>
            <div className="font-mono">{stats.config.minEvent}</div>
          </div>
          <div>
            <div className="text-gray-500">Min Location</div>
            <div className="font-mono">{stats.config.minLocation}</div>
          </div>
          <div>
            <div className="text-gray-500">Fusion Alpha</div>
            <div className="font-mono">{stats.config.fusionAlpha}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Link to="/stories" className="btn btn-primary">
          View Stories
        </Link>
        <Link to="/playground" className="btn btn-secondary">
          Config Playground
        </Link>
      </div>
    </div>
  );
}
